import { useState, useEffect, useRef } from 'react';
import { Bell, X, Archive, Check, CheckCheck, Trash2 } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { NotificationsAPI } from '../api/notifications';
import type { Notification } from '../lib/db/schema';
import { UsersAPI } from '../api/users';
import { SafeHtml } from './SafeHtml';

export const NotificationBell = () => {
    const { isSignedIn, user } = useUser();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [internalUserId, setInternalUserId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'inbox' | 'archive'>('inbox');
    const panelRef = useRef<HTMLDivElement>(null);

    // Obtener el ID interno del usuario desde Clerk ID
    useEffect(() => {
        if (isSignedIn && user?.id) {
            UsersAPI.getByClerkId(user.id).then((res) => {
                if (res.success && res.data?.id) {
                    setInternalUserId(res.data.id);
                }
            });
        }
    }, [isSignedIn, user?.id]);

    // Cargar notificaciones
    const fetchNotifications = async () => {
        if (!internalUserId) return;
        setLoading(true);
        const res = await NotificationsAPI.getForUser(internalUserId);
        if (res.success && res.data) {
            // Guardar todas, tanto activas como archivadas (si la API retorna las archivadas)
            setNotifications(res.data);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (internalUserId) {
            fetchNotifications();
            // Polling cada 60 segundos
            const interval = setInterval(fetchNotifications, 60000);
            return () => clearInterval(interval);
        }
    }, [internalUserId]);

    // Cerrar al hacer click fuera del panel
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Separar notificaciones por pestaña
    const inboxNotifications = notifications.filter((n) => !n.isArchived);
    const archiveNotifications = notifications.filter((n) => n.isArchived);
    const displayedNotifications = activeTab === 'inbox' ? inboxNotifications : archiveNotifications;

    const unreadCount = inboxNotifications.filter((n) => !n.isRead).length;

    const handleOpen = async () => {
        setIsOpen((prev) => !prev);
        // Al abrir, marcar todas como leídas
        if (!isOpen && internalUserId && unreadCount > 0) {
            await NotificationsAPI.markAllRead(internalUserId);
            setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        }
    };

    const handleMarkRead = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        await NotificationsAPI.markRead(id);
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
    };

    const handleArchive = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        await NotificationsAPI.archive(id);
        // Marcamos localmente como archivada sin eliminarla del array general
        setNotifications((prev) =>
            prev.map((n) => n.id === id ? { ...n, isArchived: true, isRead: true } : n)
        );
    };

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        await NotificationsAPI.delete(id);
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    };

    const formatDate = (date: Date | null | string) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('es-UY', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (!isSignedIn) return null;

    return (
        <div className="relative" ref={panelRef}>
            {/* Botón campana */}
            <button
                onClick={handleOpen}
                aria-label="Notificaciones"
                className="relative p-2 rounded-lg transition-all duration-200 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-md">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Panel desplegable */}
            {isOpen && (
                <>
                    {/* Overlay para móvil para cerrar al hacer click fuera (opcional pero ayuda UX) */}
                    <div 
                        className="fixed inset-0 z-40 sm:hidden" 
                        onClick={() => setIsOpen(false)} 
                    />
                    
                    <div className="fixed left-4 right-4 top-24 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-96 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-100 dark:border-slate-700 z-50 overflow-hidden max-w-[calc(100vw-2rem)] sm:max-w-none">
                        {/* Header del panel */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                        <div className="flex items-center gap-2">
                            <Bell className="w-4 h-4 text-blue-500" />
                            <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">
                                Notificaciones
                            </span>
                            {unreadCount > 0 && (
                                <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                                    {unreadCount}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex w-full border-b border-gray-100 dark:border-slate-700">
                        <button
                            onClick={() => setActiveTab('inbox')}
                            className={`flex-1 py-2 text-sm font-medium transition-colors ${activeTab === 'inbox'
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/30 dark:bg-slate-800'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-slate-700/50'
                                }`}
                        >
                            Bandeja de Entrada
                        </button>
                        <button
                            onClick={() => setActiveTab('archive')}
                            className={`flex-1 py-2 text-sm font-medium transition-colors ${activeTab === 'archive'
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/30 dark:bg-slate-800'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-slate-700/50'
                                }`}
                        >
                            Archivados
                        </button>
                    </div>

                    {/* Lista de notificaciones */}
                    <div className="overflow-y-auto max-h-96">
                        {loading ? (
                            <div className="flex items-center justify-center py-10">
                                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : displayedNotifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-2">
                                <Bell className="w-10 h-10 text-gray-300 dark:text-slate-600" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {activeTab === 'inbox' ? 'No tenés notificaciones' : 'No hay notificaciones archivadas'}
                                </p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-50 dark:divide-slate-700">
                                {displayedNotifications.map((n) => (
                                    <li
                                        key={n.id}
                                        className={`px-4 py-3 transition-colors duration-150 ${!n.isRead
                                            ? 'bg-blue-50/60 dark:bg-blue-900/20'
                                            : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'
                                            }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            {/* Indicador no leído */}
                                            {!n.isRead && (
                                                <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" />
                                            )}
                                            <div className={`flex-1 min-w-0 ${n.isRead ? 'pl-4' : ''}`}>
                                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                                                    {n.title}
                                                </p>
                                                <SafeHtml
                                                    html={n.message}
                                                    mode="rich"
                                                    className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-relaxed overflow-hidden prose prose-sm max-w-none dark:prose-invert [&>p]:mb-1 [&>p:last-child]:mb-0 [&>ul]:my-1 [&>ol]:my-1"
                                                />
                                                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                                                    {formatDate(n.createdAt)}
                                                </p>
                                            </div>
                                            {/* Acciones */}
                                            <div className="flex flex-col gap-3 flex-shrink-0 justify-center pl-2">
                                                {!n.isRead && (
                                                    <button
                                                        onClick={(e) => handleMarkRead(n.id, e)}
                                                        title="Marcar como leída"
                                                        className="p-1.5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 transition-colors shadow-sm"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                {activeTab === 'inbox' && (
                                                    <button
                                                        onClick={(e) => handleArchive(n.id, e)}
                                                        title="Archivar"
                                                        className="p-1.5 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50 transition-colors shadow-sm"
                                                    >
                                                        <Archive className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => handleDelete(n.id, e)}
                                                    title="Eliminar"
                                                    className="p-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors shadow-sm"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Footer - marcar todas leídas */}
                    {activeTab === 'inbox' && inboxNotifications.some((n) => !n.isRead) && (
                        <div className="px-4 py-2 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                            <button
                                onClick={async () => {
                                    if (internalUserId) {
                                        await NotificationsAPI.markAllRead(internalUserId);
                                        setNotifications((prev) =>
                                            prev.map((n) => ({ ...n, isRead: true }))
                                        );
                                    }
                                }}
                                className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                            >
                                <CheckCheck className="w-3.5 h-3.5" />
                                Marcar todas como leídas
                            </button>
                        </div>
                    )}
                </div>
                </>
            )}
        </div>
    );
};

export default NotificationBell;
